import React from 'react';
import { Typography, Grid, Box } from '@mui/material';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import { styled } from '@mui/material/styles';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import PriceTable from './PriceTable';
import usePricePeriods from '../../../hooks/usePricePeriods';
import imageDimensions from '../../../data/imageDimensions.json';
import { DEFAULT_LOCALE, normalizeLocale } from '../../../locales/languages';

const sectionHeadingStyle = {
  fontWeight: 'bold',
  fontSize: 15,
  marginTop: 2,
  marginBottom: 2,
};

const Root = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(2),
  },
}));

const CarouselContainer = styled('div')(({ theme }) => ({
  maxWidth: '100%',
  // textAlign: 'center',
  overflow: 'hidden',
  maxHeight: '1300px',
  width: '1000px',
  backgroundColor: 'rgba(255, 255, 255, 0.85)',
  backgroundImage:
    'linear-gradient(rgba(67, 16, 41, 0.55),rgba(255, 255, 255,  0.25))',
  [theme.breakpoints.down('md')]: {
    width: '100%',
    maxHeight: 720,
  },
  [theme.breakpoints.down('sm')]: {
    maxHeight: 520,
  },
}));

const ContentContainer = styled('div')(({ theme }) => ({
  padding: theme.spacing(2),
  overflow: 'auto',
}));

const FacilityIconsContainer = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '10px',
  justifyContent: 'center',
});

const FacilityIcon = styled('div')(({ theme }) => ({
  marginRight: theme.spacing(1),
  display: 'flex',
  alignItems: 'center',
  fontSize: 11,
  flexDirection: 'column',
}));

const IconImage = styled('img')({
  width: '24px',
  height: '24px',
  marginBottom: '0.13rem',
});

const RoomFact = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.75),
  padding: `${theme.spacing(0.75)} ${theme.spacing(1.25)}`,
  border: `1px solid ${theme.palette.secondary.light}`,
  borderRadius: 8,
  backgroundColor: 'rgba(255, 255, 255, 0.78)',
  color: theme.palette.text.primary,
  fontSize: 13,
  fontWeight: 700,
  lineHeight: 1.2,
}));

const RoomFactIconImage = styled('img')({
  width: 22,
  height: 22,
});

const StyledTypography = styled(Typography)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.secondary.main}`,
  paddingBottom: '3px',
  fontWeight: 'bold',
}));

const isPlaceholderDescription = (value) => {
  if (!value || typeof value !== 'string') {
    return true;
  }
  const normalized = value.trim().toLowerCase();
  return (
    normalized === 'description in english' ||
    normalized === 'περιγραφή στα αγγλικά'
  );
};

export default function ApartmentCard({ apartment, t, i18n }) {
  const { prices } = usePricePeriods();
  const getImageDimensions = (photo) => imageDimensions[photo] || {};
  const activeLocale = normalizeLocale(i18n.language) || DEFAULT_LOCALE;
  const capacity = apartment.capacity || 2;
  const rawDescription =
    apartment.desc?.[activeLocale] || apartment.desc?.[DEFAULT_LOCALE] || '';
  const description = isPlaceholderDescription(rawDescription)
    ? t('rooms.metaDescriptionFallback', {
        roomName: apartment.longName || apartment.name,
      })
    : rawDescription;
  const priceList = React.useMemo(() => {
    if (!prices || prices.length === 0) {
      return apartment.price || [];
    }
    return prices
      .filter((price) => price.apartmentId === apartment.id)
      .map((price) => ({
        dates: price.dates,
        minStay: price.minStay,
        rate: price.rate,
      }));
  }, [apartment.id, apartment.price, prices]);

  return (
    <Root>
      <Grid container>
        <Grid item xs={12} md={6}>
          <CarouselContainer elevation={3}>
            <Carousel
              showThumbs
              showArrows
              swipeable
              dynamicHeight
              useKeyboardArrows
              showIndicators={false}
              showStatus={false}
            >
              {apartment.photos.map((photo, index) => {
                const { width, height } = getImageDimensions(photo);
                return (
                  <div key={photo}>
                    <img
                      src={photo}
                      alt={`Apartment ${apartment.name}`}
                      loading={index === 0 ? 'eager' : 'lazy'}
                      decoding="async"
                      width={width}
                      height={height}
                      style={{
                        width: '100%',
                        maxWidth: '400px',
                        height: 'auto',
                      }}
                    />
                  </div>
                );
              })}
            </Carousel>
            {/* <SlickSlider apartment={apartment} /> */}
          </CarouselContainer>
        </Grid>
        {/* <Grid container component={ContentContainer}> */}
        <Grid item xs={12} md={6}>
          <Grid container spacing={1} sx={{ px: { xs: 0.5, sm: 3 } }}>
            <Grid item xs={12}>
              <Typography
                variant="body1"
                sx={{ fontSize: { xs: 14, sm: 16 }, lineHeight: 1.55 }}
              >
                {description}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  mt: 1.5,
                  fontWeight: 700,
                  color: 'secondary.dark',
                  fontSize: { xs: 14, sm: 16 },
                  lineHeight: 1.4,
                }}
              >
                {t('rooms.directBookingLine')}
              </Typography>
              <Box
                sx={{
                  mt: 2,
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  justifyContent: { xs: 'center', md: 'flex-start' },
                }}
              >
                <RoomFact>
                  <RoomFactIconImage src="/facility-icons/size.png" alt="" />
                  {t('apModal.size')}: {apartment.size} m²
                </RoomFact>
                <RoomFact>
                  <PeopleAltIcon sx={{ fontSize: 22 }} />
                  {t('rooms.capacityLabel', { count: capacity })}
                </RoomFact>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <StyledTypography variant="subtitle1" sx={sectionHeadingStyle}>
                {t('apModal.rf')}
              </StyledTypography>{' '}
              <FacilityIconsContainer>
                {apartment.kitchen && (
                  <FacilityIcon>
                    <IconImage
                      src="/facility-icons/kitchen.png"
                      alt="Kitchen"
                    />
                    {t('apModal.k')}
                  </FacilityIcon>
                )}
                {apartment.private_bathroom && (
                  <FacilityIcon>
                    <IconImage src="/facility-icons/bath.png" alt="Bathroom" />
                    {t('apModal.pb')}
                  </FacilityIcon>
                )}
                {apartment.air_conditioning && (
                  <FacilityIcon>
                    <IconImage
                      src="/facility-icons/air-conditioning.png"
                      alt="air_conditionin"
                    />
                    {t('apModal.ac')}
                  </FacilityIcon>
                )}
                {apartment.seaview && (
                  <FacilityIcon>
                    <IconImage
                      src="/facility-icons/seaview.png"
                      alt="Seaview"
                    />
                    {t('apModal.sv')}
                  </FacilityIcon>
                )}
                {apartment.gardenview && (
                  <FacilityIcon>
                    <IconImage
                      src="/facility-icons/gardenview.png"
                      alt="gardenview"
                    />
                    {t('apModal.gv')}
                  </FacilityIcon>
                )}
                {apartment.cityview && (
                  <FacilityIcon>
                    <IconImage
                      src="/facility-icons/cityview.png"
                      alt="cityview"
                    />
                    {t('apModal.cv')}
                  </FacilityIcon>
                )}
                {apartment.flat_screen_tv && (
                  <FacilityIcon>
                    <IconImage src="/facility-icons/tv.png" alt="tv" />
                    {t('apModal.tv')}
                  </FacilityIcon>
                )}
                {apartment.extra_large_double_beds > 0 && (
                  <FacilityIcon>
                    <IconImage
                      src="/facility-icons/large_bed.png"
                      alt="large-bed"
                    />
                    {apartment.extra_large_double_beds} {t('apModal.elb')}
                  </FacilityIcon>
                )}
                {apartment.double_beds > 0 && (
                  <FacilityIcon>
                    <IconImage
                      src="/facility-icons/double_bed.png"
                      alt="double-bed"
                    />
                    {apartment.double_beds} {t('apModal.db')}
                  </FacilityIcon>
                )}
                {apartment.single_bed > 0 && (
                  <FacilityIcon>
                    <IconImage
                      src="/facility-icons/single_bed.png"
                      alt="single-bed"
                    />
                    {apartment.single_beds} {t('apModal.sb')}
                  </FacilityIcon>
                )}
                {apartment.single_sofas > 0 && (
                  <FacilityIcon>
                    <IconImage
                      src="/facility-icons/single_sofa.png"
                      alt="single-sofa"
                    />
                    {apartment.single_sofas} {t('apModal.ss')}
                  </FacilityIcon>
                )}
                {apartment.double_sofas > 0 && (
                  <FacilityIcon>
                    <IconImage
                      src="/facility-icons/double_sofa.png"
                      alt="sofa-bed"
                    />
                    {apartment.double_sofas} {t('apModal.ds')}
                  </FacilityIcon>
                )}
              </FacilityIconsContainer>
            </Grid>
            <Grid item xs={12}>
              <StyledTypography variant="subtitle1" sx={sectionHeadingStyle}>
                {t('apModal.of')}
              </StyledTypography>
              <FacilityIconsContainer>
                {apartment.balcony && (
                  <FacilityIcon>
                    <IconImage
                      src="/facility-icons/balcony.png"
                      alt="balcony"
                    />
                    {t('apModal.b')}
                  </FacilityIcon>
                )}
                {apartment.terrace && (
                  <FacilityIcon>
                    <IconImage
                      src="/facility-icons/terrace.png"
                      alt="terrace"
                    />
                    {t('apModal.t')}
                  </FacilityIcon>
                )}
              </FacilityIconsContainer>
            </Grid>
            <Grid item xs={12}>
              <StyledTypography variant="subtitle1" sx={sectionHeadingStyle}>
                {t('apModal.p')}
              </StyledTypography>
              <PriceTable prices={priceList} t={t} />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Root>
  );
}
